/**
 * Small colour toolkit for tenant theming: parse, convert, mix and contrast.
 * Pure functions, no DOM. WCAG 2 relative luminance for contrast.
 */

export interface Rgba {
  r: number;
  g: number;
  b: number;
  a: number;
}

export interface Hsl {
  /** 0 to 360 */
  h: number;
  /** 0 to 1 */
  s: number;
  /** 0 to 1 */
  l: number;
}

const clamp = (n: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, n));

/** Parses `#rgb`, `#rrggbb`, `rgb()` and `rgba()`. Null when it is neither. */
export function parseColour(input: string): Rgba | null {
  const v = input.trim().toLowerCase();
  const hex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/.exec(v);
  if (hex) {
    const h = hex[1].length === 3 ? hex[1].replace(/./g, (c) => c + c) : hex[1];
    return {
      r: parseInt(h.slice(0, 2), 16),
      g: parseInt(h.slice(2, 4), 16),
      b: parseInt(h.slice(4, 6), 16),
      a: 1,
    };
  }
  const fn = /^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)(?:\s*[,/]\s*([\d.]+%?))?\s*\)$/.exec(v);
  if (fn) {
    const alpha = fn[4] === undefined ? 1 : fn[4].endsWith("%") ? parseFloat(fn[4]) / 100 : parseFloat(fn[4]);
    return { r: +fn[1], g: +fn[2], b: +fn[3], a: alpha };
  }
  return null;
}

export function isHexColour(input: string | null | undefined): input is string {
  return typeof input === "string" && /^#[0-9a-fA-F]{6}$/.test(input.trim());
}

export function rgbToHsl({ r, g, b }: Rgba): Hsl {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  const d = max - min;
  if (d === 0) return { h: 0, s: 0, l };
  const s = d / (1 - Math.abs(2 * l - 1));
  let h: number;
  if (max === rn) h = ((gn - bn) / d) % 6;
  else if (max === gn) h = (bn - rn) / d + 2;
  else h = (rn - gn) / d + 4;
  h *= 60;
  if (h < 0) h += 360;
  return { h, s, l };
}

export function hslToRgb({ h, s, l }: Hsl, a = 1): Rgba {
  const hh = ((h % 360) + 360) % 360;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((hh / 60) % 2) - 1));
  const m = l - c / 2;
  let r = 0;
  let g = 0;
  let b = 0;
  if (hh < 60) [r, g, b] = [c, x, 0];
  else if (hh < 120) [r, g, b] = [x, c, 0];
  else if (hh < 180) [r, g, b] = [0, c, x];
  else if (hh < 240) [r, g, b] = [0, x, c];
  else if (hh < 300) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];
  return { r: Math.round((r + m) * 255), g: Math.round((g + m) * 255), b: Math.round((b + m) * 255), a };
}

const hex2 = (n: number) => clamp(Math.round(n), 0, 255).toString(16).padStart(2, "0");

/** `#rrggbb` for opaque colours, `rgba(...)` when alpha is below 1. */
export function toCss({ r, g, b, a }: Rgba): string {
  if (a >= 1) return `#${hex2(r)}${hex2(g)}${hex2(b)}`;
  return `rgba(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)}, ${+a.toFixed(3)})`;
}

export function hslCss(h: number, s: number, l: number, a = 1): string {
  return toCss(hslToRgb({ h, s: clamp(s, 0, 1), l: clamp(l, 0, 1) }, a));
}

export function withAlpha(c: Rgba, a: number): string {
  return toCss({ ...c, a });
}

export function mix(a: Rgba, b: Rgba, t: number): Rgba {
  return {
    r: a.r + (b.r - a.r) * t,
    g: a.g + (b.g - a.g) * t,
    b: a.b + (b.b - a.b) * t,
    a: a.a + (b.a - a.a) * t,
  };
}

function channel(v: number): number {
  const c = v / 255;
  return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

export function luminance({ r, g, b }: Rgba): number {
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

/** WCAG contrast ratio between two opaque colours (1 to 21). */
export function contrast(a: Rgba, b: Rgba): number {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

const WHITE: Rgba = { r: 255, g: 255, b: 255, a: 1 };

/**
 * Moves `base` darker (or lighter) in lightness, keeping hue and saturation,
 * until it reaches `min` contrast against `against`. Returns the first
 * lightness that passes, so the result stays as close to `base` as possible.
 */
export function ensureContrast(base: Hsl, against: Rgba, min: number, direction: "darker" | "lighter"): Hsl {
  const step = direction === "darker" ? -0.01 : 0.01;
  let l = base.l;
  for (let i = 0; i < 100; i += 1) {
    if (contrast(hslToRgb({ ...base, l }), against) >= min) return { ...base, l };
    const next = l + step;
    if (next < 0 || next > 1) break;
    l = next;
  }
  return { ...base, l: clamp(l, 0, 1) };
}

/** Readable text colour on a fill: white or near black, whichever contrasts more. */
export function readableOn(fill: Rgba): "light" | "dark" {
  return contrast(fill, WHITE) >= contrast(fill, { r: 6, g: 22, b: 21, a: 1 }) ? "light" : "dark";
}

export { WHITE };
